import React from 'react';
import { useMutation } from '@apollo/client';
import {
  CompositeNavigationProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { Platform } from 'react-native';
import Snackbar from '~/components/Snackbar';
import { useAuth } from '~/context/auth';
import { NO_PAG_LIST_OFFSET } from '~/data/constants';
import { DATA_POLICY, TERM } from '~/data/derivedConstants';
import useAppLocale from '~/data/hooks/useAppLocale';
import { Country } from '~/data/models/marketProfile';
import { CREATE_USER } from '~/data/operations/auth';
import { ADD_USER_TO_GROUP, GROUP_USERS } from '~/data/operations/group';
import { GENERATE_SHOPIFY_URL } from '~/data/operations/shop';
import { clearAuth } from '~/data/storage';
import { Status, UserType } from '~/data/types/graphql';
import { getLanguageFromLocale } from '~/data/utils';
import {
  RootStackParamList,
  LobbyStackParamList,
  AuthStackParamList,
} from '~/navigation/types';
import { redirectToShop } from '~/utils/helpers';
import SignUpTermLayout from './layout';

type SignUpTermNavProp = CompositeNavigationProp<
  StackNavigationProp<AuthStackParamList, 'SignUpTerm'>,
  CompositeNavigationProp<
    StackNavigationProp<RootStackParamList>,
    StackNavigationProp<LobbyStackParamList>
  >
>;
type SignUpTermRouteProp = RouteProp<AuthStackParamList, 'SignUpTerm'>;

const platform = Platform.OS;
export default function SignUpTerm(): JSX.Element {
  const navigation = useNavigation<SignUpTermNavProp>();
  const {
    params: { g: groupId, t: groupToken, r: inviteCode, shopReturnUrl },
  } = useRoute<SignUpTermRouteProp>();
  const hasInviteParams = groupId && groupToken;

  const { locale, onChangeLocale } = useAppLocale({
    onReload: () =>
      navigation.replace('SignUpTerm', {
        g: groupId,
        t: groupToken,
        r: inviteCode,
      }),
  });

  const {
    loading: authLoading,
    country,
    onUpdateCountry,
    onAuth,
    onAuthGroup,
    signUpData,
    onClearSignUpUser,
  } = useAuth();

  const [createUser, { loading: createUserLoading }] = useMutation(CREATE_USER);

  const [addUserToGroup, { loading: addUserToGroupLoading }] =
    useMutation(ADD_USER_TO_GROUP);

  const [generateShopifyUrl] = useMutation(GENERATE_SHOPIFY_URL);

  async function onCreateUser(
    dataPolicyAcceptance: boolean,
    termsAcceptance: boolean,
    subscribeNewsletter: boolean,
  ) {
    try {
      const communicationLanguage = getLanguageFromLocale();

      const { firstName, lastName, email, phoneCountryCode, phone, password } =
        signUpData;
      const { data: createUserData } = await createUser({
        variables: {
          input: {
            firstName: firstName,
            lastName: lastName,
            email: email,
            phoneCountryCode: phoneCountryCode,
            phone: phone,
            password: password,
            passwordConfirmation: password,
            dataPolicyAcceptance,
            dataPolicyVersion: DATA_POLICY.version,
            termsAcceptance,
            termsVersion: TERM.version,
            subscribeNewsletter,
            communicationLanguage,
            inviteCode,
            countryOfResidence: country,
            registrationPlatform: platform,
          },
        },
      });
      const authUser = createUserData?.createUser?.user;
      const userId = authUser?.id;

      if (!userId) {
        throw new Error('invalid data');
      }
      await onAuth(email, password);

      if (hasInviteParams) {
        handleInvite(userId, groupId, groupToken);
        return;
      }

      await onClearSignUpUser();

      if (shopReturnUrl) {
        const { data } = await generateShopifyUrl({
          variables: {
            input: {
              returnUrl: decodeURIComponent(shopReturnUrl),
            },
          },
        });

        if (data?.generateShopifyUrl?.redirectUrl) {
          redirectToShop(data?.generateShopifyUrl?.redirectUrl);
          return;
        }
      }

      navigation.navigate('SignUpSuccess');
    } catch (e) {
      clearAuth();
      if (e instanceof Error) {
        Snackbar.show(e.message);
      }
    }
  }

  async function handleInvite(
    userId: string,
    groupId: string,
    groupToken: string,
  ) {
    try {
      const { data: addUserToGroupData } = await addUserToGroup({
        variables: {
          input: { groupId, userId, role: UserType.STUDENT, token: groupToken },
        },
        refetchQueries: [
          {
            query: GROUP_USERS,
            variables: {
              user: userId,
              first: NO_PAG_LIST_OFFSET,
            },
          },
        ],
      });
      const groupUser = addUserToGroupData?.addUserToGroup?.userGroup;
      if (groupUser?.status === Status.ACCEPTED) {
        await onAuthGroup(groupId, groupUser.id);
      }

      if (Platform.OS === 'web') {
        navigation.popToTop();
        return;
      }
      navigation.navigate('InviteSuccess', { groupId });
    } catch (e) {
      if (e instanceof Error) {
        Snackbar.show(e.message);
      }
      navigation.navigate('SignUpSuccess');
    }
  }

  return (
    <SignUpTermLayout
      inputProgress={4}
      loading={createUserLoading || authLoading || addUserToGroupLoading}
      selectedLocale={locale}
      onBack={() => navigation.goBack()}
      onShowPolicy={() =>
        navigation.navigate('Browser', {
          title: DATA_POLICY.title,
          url: DATA_POLICY.url,
        })
      }
      onShowTerm={() =>
        navigation.navigate('Browser', {
          title: TERM.title,
          url: TERM.url,
        })
      }
      onNext={(dataPolicyAcceptance, termsAcceptance, subscribeNewsletter) =>
        onCreateUser(dataPolicyAcceptance, termsAcceptance, subscribeNewsletter)
      }
      onSelectedLocale={onChangeLocale}
      onUpdateCountry={onUpdateCountry}
      country={country as Country}
    />
  );
}
